/* eslint-disable react/no-danger */
import Dots from '@jo-v1/components/Loading/Dots';
import { PiiFormSection } from '@jo-v1/components/PiiFormSection';
import { validateInputs } from '@jo-v1/components/PiiFormSection/validateInputs';
import TcpaSection from '@jo-v1/features/TcpaSection';
import Testimonial from '@jo-v2/components/Testimonial';
import { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { FaStar, FaThumbsUp } from 'react-icons/fa';
import { HiLocationMarker, HiCheckCircle } from 'react-icons/hi';
import { useSelector, useDispatch } from 'react-redux';

import AccordionItem from '@core/blocks/eduResults/components/Revamp/AccordionItem';
import { LEAD_SUBMISSION_STEPS } from '@core/constants';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useSchoolConfig from '@core/hooks/useSchoolConfig';
import { selectContactId } from '@core/reducers/contactSlice';
// Reducer's selectors/actions
import { setHeclidMapEntry, selectProductCorrelationIdMap, selectHeclidMap } from '@core/reducers/eventingSlice';
import { selectAllInputs, selectInput, setInputs } from '@core/reducers/inputsSlice';
import { addSubmission, hideLeadDelivery } from '@core/reducers/matchesSlice';
import { submitLead } from '@core/services/leadDelivery';
import { mobiusRedirect } from '@core/services/mobius';
import { PageActions } from '@core/services/newRelic';
// Components
import PulsingDotLoader from '@core/shared/components/PulsingDotLoader';
import SubmissionStatus from '@core/shared/features/SubmissionStatus';
import type { VoyagerResult } from '@core/ts/results';
// Utils, Hooks & Services
import cn from '@core/utils/classNames';
import { phoneValidationFeatureFlagWrapper } from '@core/utils/functionWrappers/phoneValidationFeatureFlagWrapper';
import getToken from '@core/utils/getToken';
import timer from '@core/utils/timer';

// Styles
import styles from './JoPiiConfirmation.module.css';

const { ADDITIONAL_QUESTIONS, EXIT_STRATEGY, PII_CONFIRMATION } = LEAD_SUBMISSION_STEPS;
type JoPiiConfirmationProps = {
  setPiiLeadErrors: Dispatch<SetStateAction<{ field: string }[]>>;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  piiLeadErrors: { field: string }[];
  step: string;
  setStep: Dispatch<SetStateAction<string>>;
  currentProgram: VoyagerResult;
  prqSchema: any;
  formErrored: () => void;
  editMode: boolean;
  tcpa: string;
  tcpaLoading: boolean;
  tcpaErrorMsg?: string;
  prqLoading: boolean;
};

// Gets or previews lead info
const JoPiiConfirmation = ({
  step,
  setStep,
  currentProgram,
  prqSchema,
  setPiiLeadErrors,
  piiLeadErrors,
  setEditMode,
  formErrored,
  editMode,
  tcpa,
  tcpaLoading,
  tcpaErrorMsg,
  prqLoading,
}: JoPiiConfirmationProps) => {
  // Inputs State properties
  const inputs = useSelector(selectAllInputs);
  const startTime = useSelector((state) => selectInput(state, 'startTime'));
  // Eventing State properties
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  const formInputs = useSelector(selectAllInputs);
  const [submissionStatus, setSubmissionStatus] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isHeclidReady = heclidMap[currentProgram?.program.id] !== undefined;
  const [formError, setFormError] = useState<{ field: string }[]>([]);

  // First Name State
  const [firstName, setFirstName] = useState(formInputs.firstName?.value);
  // Last Name State
  const [lastName, setLastName] = useState(formInputs.lastName?.value);
  // Email State
  const [email, setEmail] = useState(formInputs.email?.value);
  // Phone State
  const [phone, setPhone] = useState(formInputs.phone?.value);

  const [hasPII, setHasPII] = useState<boolean>(true);
  const flags = useFeatureFlags();
  // Action Dispatcher
  const dispatch = useDispatch();

  const contactId = useSelector(selectContactId);

  const schoolConfig = useSchoolConfig(currentProgram?.school?.id);

  const handleSubmit = async () => {
    setFormError([]);
    setIsSubmitting(true);

    const formErrors = await validateInputs(firstName, lastName, email, phone);
    if (formErrors.length) {
      setFormError(formErrors);
      setIsSubmitting(false);
      return;
    }

    dispatch(
      setInputs([
        { key: 'firstName', value: firstName },
        { key: 'lastName', value: lastName },
        { key: 'email', value: email },
        { key: 'phone', value: phone },
      ])
    );
    if (prqSchema) {
      setStep(ADDITIONAL_QUESTIONS);
      return;
    }

    const actionParams = {
      ...flags,
      anonymousId: window._Cohesion.anonymousId,
      correlationId: productCorrelationIdMap[currentProgram?.program?.id],
    };
    PageActions.LeadAttempt(actionParams);
    const leadInputs = {
      ...inputs,
      tcpaText: { key: 'tcpaText', value: schoolConfig?.data?.tcpa },
    };
    const res = await phoneValidationFeatureFlagWrapper({
      wrappedFunction: () =>
        submitLead(
          currentProgram,
          leadInputs,
          heclidMap[currentProgram?.program?.id],
          productCorrelationIdMap[currentProgram?.program?.id],
          contactId
        ),
      inputs: leadInputs,
      flag: !!flags?.phoneValidation,
    });
    if (res?.message === 'invalid_phone_line_type_intelligence') {
      setIsSubmitting(false);
      setEditMode(true);
      setPiiLeadErrors((state) => [...state, { field: 'phone' }]);
      setSubmissionStatus('');
      formErrored();
      return;
    }

    if (res?.message) {
      setIsSubmitting(false);
      const error = JSON.parse(res.message);
      const status = error?.message === 'qualifications_not_met' ? 'UNQUALIFIED' : 'ERROR';
      setSubmissionStatus(status);
      await timer(2000);
      dispatch(hideLeadDelivery());
    } else {
      PageActions.LeadSuccess(actionParams);
      dispatch(addSubmission(currentProgram));
      setStep(EXIT_STRATEGY);
    }
  };

  useEffect(() => {
    if (step === PII_CONFIRMATION)
      mobiusRedirect({
        idToken: getToken(),
        productCorrelationId: productCorrelationIdMap[currentProgram?.program?.id],
        link: currentProgram?.url,
        trackingContextOverride: {
          formatType: 'app',
          formatSubtype: 'jo-grid',
        },
      }).then(({ heclid, error }) => {
        if (!error) {
          dispatch(setHeclidMapEntry({ key: currentProgram?.program?.id, value: heclid }));
        }
      });
  }, []);

  useEffect(() => {
    if (!inputs.firstName?.value || !inputs.lastName?.value || !inputs.email?.value || !inputs.phone?.value) {
      setEditMode(true);
    }
  }, [inputs]);

  useEffect(() => {
    if (!firstName || !lastName || !email || !phone) {
      setHasPII(false);
    }
    if (firstName && lastName && email && phone) {
      setHasPII(true);
    }
  }, [firstName, lastName, email, phone]);

  if (tcpaLoading)
    return (
      <section className={styles.loadingContainer}>
        {tcpaErrorMsg ? (
          <h2>{tcpaErrorMsg}</h2>
        ) : (
          <>
            <h2>Loading...</h2>
            <Dots />
          </>
        )}
      </section>
    );

  const ctaText = 'Learn More';

  const buttonText = isSubmitting || !isHeclidReady || prqLoading ? <PulsingDotLoader /> : ctaText;

  const heading = !!flags?.noPii && startTime === 0 ? 'Add your contact details:' : 'Confirm your contact details:';
  return submissionStatus ? (
    <SubmissionStatus status={submissionStatus} schoolName={currentProgram?.school?.name} />
  ) : (
    <form onSubmit={(e) => e.preventDefault()} data-tf-element-role="offer" className={styles.piiConfirmation}>
      <div className={styles.header}>
        <img
          src={
            currentProgram?.school?.slug
              ? `https://simple-storage-server.highereducation.com/${currentProgram.school.slug}.png?w=130&h=80`
              : undefined
          }
          alt={currentProgram?.school?.name}
        />
        <div className={styles.schoolInfoContainer}>
          <h4 className={styles.title}>{currentProgram?.school?.name}</h4>
          {currentProgram?.school?.city && currentProgram?.school?.state && (
            <div className={styles.location}>
              <span className={styles.locationIcon}>
                <HiLocationMarker size={16} />
              </span>{' '}
              {currentProgram.school.city}, {currentProgram.school.state}
            </div>
          )}
        </div>
      </div>
      <div className={styles.bodyWrapper}>
        <div className={styles.body}>
          <div className={styles.bestMatchWrapperMobile}>
            {currentProgram?.__position === 1 && (
              <span className={styles.starIconContainer}>
                <FaStar size={9} color="#FFCC00" />
              </span>
            )}
            {currentProgram?.__position === 1 ? 'Best Match' : 'Great Match'}
          </div>
          <div className={styles.piiSection}>
            <p className={styles.subtitle}>{heading}</p>

            {/* View/Edit personal info */}
            <div className={styles.infoPanel}>
              <PiiFormSection
                program={currentProgram.program}
                header=""
                setEditMode={setEditMode}
                editMode={editMode}
                piiLeadErrors={piiLeadErrors}
                location="single-program"
                onSubmit={handleSubmit}
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setEmail={setEmail}
                setPhone={setPhone}
                formErrors={formError}
                setFormErrors={setFormError}
              />
            </div>
            <p className={styles.piiSubtitle}>
              Start a conversation with a school admin by submitting your contact information. Once you submit, you’ll
              be able to start your application.
            </p>
            <div className={styles.submitBtnSection}>
              <button
                className={cn(
                  styles.submitInfoBtn,
                  (!isHeclidReady || !hasPII || prqLoading) && styles.disabledSubmitInfoBtn
                )}
                disabled={!isHeclidReady || isSubmitting || !hasPII || prqLoading}
                id="reward_click_lds"
                data-testid="edumatch-leaddelivery-submit"
                data-tf-element-role="submit"
                onClick={handleSubmit}
                type="button"
              >
                {buttonText}
              </button>
              <p className={styles.ctaSubtitle}>
                Your information will never be shared with a school without your consent.
              </p>
              <TcpaSection title="" tcpa={tcpa ? [tcpa] : null} />
            </div>
          </div>
        </div>
        <div className={styles.learnMoreBottomSection}>
          <div className={styles.bestMatchWrapperDesktop}>
            {currentProgram?.__position === 1 && (
              <span className={styles.starIconContainer}>
                <FaThumbsUp size={9} color="#FFCC00" />
              </span>
            )}
            <span>Recommended</span>
          </div>
          <h3 className={styles.learnMoreTitle}>
            <span className={styles.checkCircleIcon}>
              <HiCheckCircle size={20} />
            </span>
            Want to learn more?
          </h3>
          <p>
            Submit your contact information to show this school you’re interested! You can even start your application
            on the next screen.
          </p>
          <h4 className={styles.programTitle}>{currentProgram?.program?.name}</h4>
          <div className={styles.stepsTrackerWrapper}>
            <Testimonial />
          </div>
          <div className={styles.highlightsContent}>
            <h3 className={styles.schoolHighlightsTitle}>School Highlights</h3>
            <p className={styles.highlightsContentIntro}>{currentProgram.program.snippet}</p>
            <div className={styles.highlightsList}>
              {schoolConfig?.data?.valueProps.map(
                (highlight: { title: string; description: string }, index: number) => (
                  <AccordionItem
                    key={`${highlight.title}-${index}`}
                    title={highlight.title}
                    description={highlight.description}
                    index={index}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default JoPiiConfirmation;
